import React, { useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router";
import CloseIcon from "@mui/icons-material/Close";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { Dialog } from "./Dialog";
import { useAuth } from "contexts/AuthProvider";
import { axiosInstance } from "contexts/axiosApiInstance";
import { UserAccountResponse } from "components/entities";
import { SectionRule } from "components/common/SectionRule";
import { ThemePicker } from "components/common/ThemePicker";
import { TooltipButton } from "components/common/TooltipButton";
import { useElementRect } from "components/hooks/useElementRect";
import { useOrgContextStore } from "@/components/main/stores/orgContextStore";
import { formatDayMonthTime } from "@/lib/dates";
import { createLogger } from "debug/DebugingLogger";
import { showNotificationGlobal } from "stores/useNotificationStore";
import { ProfileHeader } from "./settings/ProfileHeader";
import { OrgList } from "./settings/OrgList";
import { NotificationSwitches } from "./settings/NotificationSwitches";
import { DeleteAccountView } from "./settings/account-deletion/DeleteAccountView";

const log = createLogger("SettingsDialog");

// Resolved when read: the rail that opens the card lives beside the pane.
const PANE_REF: React.RefObject<HTMLElement | null> = {
  get current() {
    return document.querySelector<HTMLElement>("[data-main-pane]");
  },
};

/// The scheduled account's deadline, off the personal org's server-owned
/// `purge_at` (D55).
const ScheduledFooter: React.FC = () => {
  const { setAccountStatus } = useAuth();
  const purgeAt = useOrgContextStore(
    (s) => s.orgs.find((o) => o.is_personal)?.purge_at,
  );
  const refreshOrgs = useOrgContextStore((s) => s.refreshOrgs);
  const [isCancelling, setIsCancelling] = useState(false);

  const cancel = async () => {
    setIsCancelling(true);
    try {
      await axiosInstance.post("/action/cancel_account_deletion");
      setAccountStatus("active");
      void refreshOrgs();
      showNotificationGlobal("Account deletion cancelled", "success");
    } catch (error) {
      log.error("Failed to cancel account deletion: %o", error);
      showNotificationGlobal("Failed to cancel account deletion", "error");
    } finally {
      setIsCancelling(false);
    }
  };

  const hoursLeft = purgeAt
    ? Math.max(0, Math.ceil((Date.parse(purgeAt) - Date.now()) / 3_600_000))
    : null;

  return (
    <div className="settings-foot is-scheduled" role="status">
      <WarningAmberOutlinedIcon sx={{ fontSize: 14 }} aria-hidden />
      <span>
        {purgeAt
          ? `Deletes ${formatDayMonthTime(purgeAt)}`
          : "Scheduled for deletion"}
      </span>
      {hoursLeft !== null && (
        <span className="settings-foot-left">· {hoursLeft} h left</span>
      )}
      <button
        type="button"
        className="settings-foot-undo"
        disabled={isCancelling}
        onClick={() => void cancel()}
      >
        Cancel deletion
      </button>
    </div>
  );
};

interface SettingsDialogProps {
  open: boolean;
  onClose: () => void;
  accountDetails: UserAccountResponse | undefined;
  onAccountUpdated: () => void;
}

export const SettingsDialog: React.FC<SettingsDialogProps> = ({
  open,
  onClose,
  accountDetails,
  onAccountUpdated,
}) => {
  const { email, accountStatus } = useAuth();
  const navigate = useNavigate();
  const switchOrg = useOrgContextStore((s) => s.switchOrg);
  const [view, setView] = useState<"home" | "delete">("home");
  const hasPane = open && !!PANE_REF.current;
  const pane = useElementRect(PANE_REF, hasPane);

  const handleClose = () => {
    setView("home");
    onClose();
  };

  const onDeleteView = view === "delete";

  return (
    <>
      {pane &&
        createPortal(
          <div
            aria-hidden
            className="naming-scrim z-40"
            style={{
              left: pane.left,
              top: pane.top,
              width: pane.width,
              height: pane.height,
            }}
          />,
          document.body,
        )}
      <Dialog
        open={open}
        bare
        anchorRef={hasPane ? PANE_REF : undefined}
        blurBackdrop={!hasPane}
        backdropStyle={hasPane ? { backgroundColor: "transparent" } : undefined}
        onClose={onDeleteView ? () => setView("home") : handleClose}
        ariaLabel="Settings"
        className="settings-card shadow-2xl"
      >
        <TooltipButton
          title="Close"
          aria-label="Close"
          variant="default"
          size="sm"
          className="settings-close"
          onClick={handleClose}
        >
          <CloseIcon />
        </TooltipButton>
        <div className="settings-views">
          <div
            className="settings-view"
            data-off={onDeleteView}
            inert={onDeleteView}
          >
            <ProfileHeader
              accountDetails={accountDetails}
              email={email}
              onAccountUpdated={onAccountUpdated}
            />
            <OrgList
              onOpenOrg={(org) => {
                handleClose();
                void switchOrg(org.org_id);
                navigate(`/organizations/${org.org_id}`);
              }}
            />
            {/* The rule's own bottom margin has no list under it to space. */}
            <section aria-label="Appearance" className="-mb-2">
              <SectionRule label="Appearance" end={<ThemePicker />} />
            </section>
            <NotificationSwitches />
            {accountStatus === "scheduledfordeletion" ? (
              <ScheduledFooter />
            ) : (
              <button
                type="button"
                className="settings-foot is-delete"
                onClick={() => setView("delete")}
              >
                Delete account
                <ChevronRightIcon sx={{ fontSize: 16 }} aria-hidden />
              </button>
            )}
          </div>
          <div
            className="settings-view is-second"
            data-off={!onDeleteView}
            inert={!onDeleteView}
          >
            <DeleteAccountView
              active={onDeleteView}
              onBack={() => setView("home")}
              onScheduled={() => setView("home")}
            />
          </div>
        </div>
      </Dialog>
    </>
  );
};
